import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { ICountryFormValues } from '../../../../@types/model/masterData/country/countryFormValues';
import { useAppSelector } from '../../../../@types/redux';
import { PillButton, ControlledTextField, ControlledMuiAutoCompleteSelect, IOptionType } from '@zz2/zz2-ui';
import { ControlledLabelledCheckBox } from '../../../../components/input/form/ControlledLabelledCheckBoxWrapper';
import { yupResolver } from '@hookform/resolvers/yup';
import CountryModelHelper from '../../../../@types/model/masterData/country/countryModelHelper';
import CustomReactHookFormDevTools from '../../../../components/devtools/ReactHookFormDevTools';

interface ICountryFormProps {
    isLoading : boolean;
    initialValues : ICountryFormValues;
    onSubmit : (values : ICountryFormValues) => void;
    onClose : () => void;
}

const CountryForm = (props : ICountryFormProps) : React.ReactElement => {
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);
    const regions = useAppSelector(x => x.masterData.regions);

    const { handleSubmit, control, formState: { isValid, isDirty }, trigger } = useForm<ICountryFormValues>({
        defaultValues: props.initialValues,
        resolver: yupResolver(CountryModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const regionOptions = useMemo<Array<IOptionType>>(() => {
        return regions
            .filter(x => x.isActive && selectedOrganizationIds?.some(y => x.organizationIds.some(z => z === y)))
            .map(x => ({ label: `(${x.code}) ${x.name}`, value: x.id }));
    }, [regions, selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div>
            <CustomReactHookFormDevTools control={control} />
            <form onSubmit={handleSubmit(props.onSubmit)}>
                <div className={'fdc p20'}>
                    <ControlledTextField
                        className={'mb15'}
                        name={'code'}
                        label={'Code'}
                        control={control}
                        defaultValue={props.initialValues.code}
                        size={'small'}
                    />
                    <ControlledTextField
                        className={'mb10'}
                        name={'name'}
                        label={'Name'}
                        control={control}
                        defaultValue={props.initialValues.name}
                        size={'small'}
                    />
                    <ControlledMuiAutoCompleteSelect
                        className={'mb10'}
                        name={'region'}
                        label={'Region'}
                        control={control}
                        options={regionOptions}
                        defaultValues={props.initialValues.region ?? null}
                    />
                    <div className={'flx1 fdr jcfs'}>
                        <ControlledLabelledCheckBox
                            label={'Active?'}
                            name={'isActive'}
                            control={control}
                            defaultValue={props.initialValues.isActive}
                            disabled={props.initialValues.isActive}
                        />
                    </div>
                    <div className={'fdr flx1 aic jcfe'}>
                        <Button className={'cpd mr20'} type={'button'} onClick={props.onClose}>
                            CANCEL
                        </Button>
                        <PillButton
                            className={'pl30 pr30'}
                            text={'SAVE'}
                            size={'small'}
                            type={'submit'}
                            color={'secondary'}
                            disabled={ !isDirty || !isValid || props.isLoading }
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default CountryForm;
